// Import Packages
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";

// Import Material UI
import { Button, TextField } from "@material-ui/core/";
import MoreVertIcon from "@material-ui/icons/MoreVert";

// Import Pages
import { TextInput } from "../../../../components/FlowConfigurationInputs/TextInput/NewTextInput";
import { Dropdown } from "../../../../components/FlowConfigurationInputs";
import { alertState } from "../../../../recoil-store/recoil-state";

const WebHookForm = ({ action, rules, setRules, isHeader = true, isVariableAdd, setOpenVariable, variables }) => {
  const { t } = useTranslation();
  const setAlert = useSetRecoilState(alertState);

  const label = "CONTENT.WEBHOOK.";
  const options = {
    dropdown: {
      data: [
        {
          value: "header",
          label: "Header",
        },
        {
          value: "query",
          label: "Query",
        },
        {
          value: "body",
          label: "Body",
        },
      ],
    },
    dropdownOperator: {
      data: [
        {
          value: "gt",
          label: "is greater than",
        },
        {
          value: "lt",
          label: "is less than",
        },
        {
          value: "eq",
          label: "is equal",
        },
        {
          value: "neq",
          label: "is not equal",
        },
        {
          value: "ie",
          label: "is exist",
        },
        {
          value: "ine",
          label: "is not exist",
        },
        {
          value: "ge",
          label: "is equal or greater than",
        },
        {
          value: "le",
          label: "is equal or less than",
        },
      ],
    },
  };

  const [refresh, setRefresh] = useState(false);
  const [edit, setEdit] = useState(true);
  const [status, setStatus] = useState('');
  const [ruleIndex, setRuleIndex] = useState(-1);

  const handleSetInput = (params, key, value, op, status) => {
    action.fields.params.value = params;
    action.fields.sourceValue.value = key;
    action.fields.targetValue.value = value;
    action.fields.operator.value = op;
    setStatus(status);
    setRefresh(!refresh);
  };

  const checkValidForm = () => {
    let isValid = true;
    if ((!action.fields.params || !action.fields.params.value) || (!action.fields.sourceValue || !action.fields.sourceValue.value.trim()) ||
      (!action.fields.operator || !action.fields.operator.value) ||
      ((action.fields.operator.value !== 'ie' && action.fields.operator.value !== 'ine') && (!action.fields.targetValue || !action.fields.targetValue.value.trim())) ||
      (!action.fields.status || !action.fields.status.value)) {
      setAlert(alert => ({
        ...alert,
        show: true,
        message: t(label + 'MANDATORY-FIELD'),
        severity: 'error'
      }));
      isValid = false;
    } else if (action.fields.status.value < 100 || action.fields.status.value > 999) {
      setAlert(alert => ({
        ...alert,
        show: true,
        message: t(label + 'STATUS-DIGIT-ERROR'),
        severity: 'error'
      }));
      isValid = false;
    }
    return isValid;
  }

  const handleAdd = () => {
    if (!checkValidForm()) {
      return;
    }
    //add value
    let control;
    if (action.fields.operator.value === "ie" || action.fields.operator.value === "ine") {
      control = true
    } else {
      control = action.fields?.targetValue?.value?.length > 0;
    }
    if (action.fields?.sourceValue?.value?.length > 0 && control) {
      if (rules.length >= 1) {
        rules[rules.length - 1].logic =
          rules[rules.length - 1].logic.includes("last") && "and";
      }

      setRules((oldState) => [
        ...oldState,
        {
          selector: action.fields.params.value,
          key: action.fields.sourceValue.value.trim(),
          op: action.fields.operator.value,
          value: action.fields.targetValue.value.trim(),
          status: action.fields.status.value,
          logic: "last",
        },
      ]);
      handleSetInput("", "", "", "", "");
    }

  };

  const handleSetData = (e, index, key) => {
    setRuleIndex(index);
    setEdit(false);
    handleSetInput(key, rules[index].key, rules[index].value, rules[index].op, rules[index].status);
  };

  const handleUpdateData = () => {
    if (!checkValidForm()) {
      return;
    }
    rules[ruleIndex].value = action.fields.targetValue.value.trim();
    rules[ruleIndex].key = action.fields.sourceValue.value.trim();
    rules[ruleIndex].op = action.fields.operator.value;
    rules[ruleIndex].selector = action.fields.params.value;
    rules[ruleIndex].status = action.fields.status.value;
    handleSetInput("", "", "", "", "");
    setRules(rules);
    setEdit(true);
    setRefresh(!refresh);
  };

  const handleDeleteData = () => {
    let n = rules.length;
    let i = ruleIndex;
    if (n > 1 && i === n - 1) {
      rules[n - 2].logic = "last";
    }
    // deleting data
    rules.splice(ruleIndex, 1);
    setEdit(true);
    setRules([...rules]);
    handleSetInput("", "", "", "");
  };
  const handleLogicChange = (e, index) => {
    let target = e.target;
    for (const el of target.parentNode.childNodes) {
      el.classList.remove("condition-active");
    }
    target.classList.add("condition-active");
    rules[index].logic = target.value;
  };

  const handleCancel = () => {
    setEdit(true);
    handleSetInput("", "", "", "");
  }

  const handleChange = (e) => {
    action.fields.status = { value: e.target.value };
    setStatus(e.target.value);
  }

  const changeOperator = (inputName, v) => {
    if (v.value === 'ie' || v.value === 'ine') {
      action.fields.targetValue = { value: '' };
    }
  }

  return (
    <div>
      {isHeader &&
        <>
          <div className="form-element">
            <p>{t(label + "WEBHOOK-RULE-NAME")}<span className="mandatory-field">*</span></p>
            <TextInput
              action={action}
              inputName="name"
              isVariableAdd={false}
            />
          </div>
          <div className="form-element">
            <p>{t(label + "DESCRIPTION")}</p>
            <TextInput
              action={action}
              inputName="description"
              isVariableAdd={false}
            />
          </div>
        </>
      }
      <div className="form-element">
        <p>{t(label + "PARAMS")}<span className="mandatory-field">*</span></p>
        <div style={{ width: "90%" }}>
          <Dropdown
            action={action}
            inputName="params"
            refresh={refresh}
            setRefresh={setRefresh}
            options={options.dropdown}
            isRequired={false}
          />
        </div>
      </div>
      <div className="form-element">
        <p>{t(label + "KEY")}<span className="mandatory-field">*</span></p>
        <TextInput
          action={action}
          inputName="sourceValue"
          refresh={refresh}
          isVariableAdd={isVariableAdd}
          setOpenVariable={setOpenVariable}
          variables={variables}
        />
      </div>
      <div className="form-element">
        <p>{t(label + "OPERATOR")}<span className="mandatory-field">*</span></p>
        <div style={{ width: "90%" }}>
          <Dropdown
            action={action}
            inputName="operator"
            refresh={refresh}
            setRefresh={setRefresh}
            options={options.dropdownOperator}
            isRequired={false}
            setField={changeOperator}
          />
        </div>
      </div>
      {!(action?.fields?.operator?.value === 'ie' || action?.fields?.operator?.value === 'ine') ?
        <div className="form-element">
          <p> {t(label + "TARGET-VALUE")}<span className="mandatory-field">*</span></p>
          <TextInput
            action={action}
            inputName="targetValue"
            refresh={refresh}
            isVariableAdd={isVariableAdd}
            setOpenVariable={setOpenVariable}
            variables={variables}
          />
        </div> : null
      }
      <div className="form-element">
        <p>{t(label + "STATUS")}<span className="mandatory-field">*</span></p>
        <div>
          <TextField
            action={action}
            onChange={handleChange}
            value={status}
            variant="outlined"
            size="small"
            type="number"
            style={{ width: '90%', fontSize: 12 }}
          />
        </div>
        <small><em>e.g.: 200, 404, 500</em></small>
      </div>
      <div
        className="form-element"
        style={{ display: "flex", justifyContent: "center" }}
      >
        {!edit ? (
          <div
            style={{
              width: "90%",
              display: "flex",
              justifyContent: "space-evenly",
              marginRight: 30
            }}
          >
            <Button
              onClick={handleDeleteData}
              style={{
                backgroundColor: "#D75E5E",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                boxShadow: "0px 0px 8px -3px rgba(0, 0, 0, 0.25);",
                minWidth: 50,
              }}
            >
              {t(label + "DELETE")}
            </Button>
            <Button
              onClick={handleUpdateData}
              style={{
                background: "#162436",
                color: "#fff",
                borderRadius: 4,
                boxShadow: "0px 0px 8px -3px rgba(0, 0, 0, 0.25);",
                minWidth: 50,
              }}
            >
              {t(label + "EDIT")}
            </Button>
            <Button
              onClick={handleCancel}
              style={{
                background: "#fff",
                border: "1px solid #162436",
                color: "#162436",
                borderRadius: 4,
                boxShadow: "0px 0px 8px -3px rgba(0, 0, 0, 0.25);",
                minWidth: 50,
              }}
            >
              {t(label + "CANCEL")}
            </Button>
          </div>
        ) : (
          <Button
            onClick={handleAdd}
            style={{
              background: "#162436",
              color: "#fff",
              borderRadius: 4,
              boxShadow: "0px 0px 8px -3px rgba(0, 0, 0, 0.25);",
              minWidth: 50,
            }}
          >
            {t(label + "ADD")}
          </Button>
        )}
      </div>
      <div
        style={{
          width: "90%",
          height: 1,
          marginTop: 5,
          marginBottom: 5,
          border: "1px solid #F6F6F6",
        }}
      />
      <div style={{ overFlowY: "auto", minHeight: 40 }}>
        {rules?.length > 0 &&
          rules?.map((rule, ruleIndex) => (
            <div key={ruleIndex}>
              <div className="form-element" style={{ width: "90%" }}>
                <ConditionPicker
                  condition={rule}
                  onClick={(e) => {
                    handleSetData(e, ruleIndex, rule.selector);
                  }}
                />
              </div>
              {rule.logic !== "last" && (
                <div className="form-element" style={{ textAlign: "center" }}>
                  <div className="condition-tabs">
                    <button
                      className={rule.logic === "and" ? "condition-active" : ""}
                      value="and"
                      onClick={(e) => {
                        handleLogicChange(e, ruleIndex);
                      }}
                    >
                      And
                    </button>
                    <button
                      className={rule.logic === "or" ? "condition-active" : ""}
                      value="or"
                      onClick={(e) => {
                        handleLogicChange(e, ruleIndex);
                      }}
                    >
                      Or
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default WebHookForm;

function ConditionPicker({ condition, onClick }) {
  const { t } = useTranslation();
  const label = "CONTENT.CONFIGURATION-FORM.CONDITION.";
  return (
    <div className="condition-picker">
      <fieldset>
        <legend>
          &nbsp;<span>{condition?.selector}</span>&nbsp;
        </legend>
        <div>
          <div>
            <span className="condition-value">{condition.key}</span>
            &nbsp;<span>{t(label + condition?.op?.toUpperCase())}</span>
            &nbsp;
            <span className="condition-value">{condition.value}</span>
          </div>
          <div>
            <button onClick={onClick}>
              <MoreVertIcon />
            </button>
          </div>
        </div>
      </fieldset>
    </div>
  );
}
